import { PopupLayer } from '@features/shared/components/PopupLayer'
import { layers } from '@features/shared/components/popupLayers'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import clsx from 'clsx'
import { DEFAULT_MATCH_MODE, type MatchMode } from '../../tables/logic/tableSearch'
import SearchMatchOptionsPanel from './SearchMatchOptionsPanel'
import { SearchPopoverFilters, SearchPopoverFrame } from './SearchPopoverParts'
import { GlobalSearchFooter } from './globalSearch/GlobalSearchFooter'
import { GlobalSearchInputBar } from './globalSearch/GlobalSearchInputBar'
import { GlobalSearchSection } from './globalSearch/GlobalSearchSection'
import { GlobalSearchStatus } from './globalSearch/GlobalSearchStatus'
import { useGlobalSearchNavigation } from '../hooks/useGlobalSearchNavigation'
import { useGlobalSearchResults } from '../hooks/useGlobalSearchResults'
import { useMatchOptionsPanel } from '../hooks/useMatchOptionsPanel'
import { useSearchInput } from '../hooks/useSearchInput'
import { useSearchResultWindows } from '../hooks/useSearchResultWindows'
import { FILTER_LABELS, RECORD_PAGE_SIZE, type FilterKey } from '../logic/constants'
import { getMatchPanelStyle } from '../logic/matchPanel'
import { isMetadataKind } from '../logic/rows'
import type { GlobalSearchModalProps, Section, ToggleAction } from '../logic/types'

// Public palette contract re-exported for header consumers (GlobalSearchBar).
export type { SearchAnchor, SearchSelection } from '..'

export default function GlobalSearchModal({
  anchor,
  onSelect,
  onClose,
  initialQuery = '',
}: GlobalSearchModalProps) {
  const { input, query, setInput, clear } = useSearchInput(initialQuery)
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all')
  const [matchMode, setMatchMode] = useState<MatchMode>(DEFAULT_MATCH_MODE)
  const matchPanel = useMatchOptionsPanel()
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const isAllTab = activeFilter === 'all'

  const results = useGlobalSearchResults({ query, matchMode, activeFilter })
  const { isPreview, isLoading, sections, visibleFilters } = results
  const navigation = useGlobalSearchNavigation({
    query,
    activeFilter,
    sections,
    isPreview,
    resultsRef,
  })
  const { activeIndex, tabSection, range } = navigation
  const getTabRow = useSearchResultWindows({
    section: tabSection,
    range,
    isPreview,
    query,
    matchParams: results.matchParams,
    scope: results.scope,
    firstMetadataRows: results.firstMetadataRows,
    metadataTotals: results.metadataTotals,
    firstRecordRows: results.firstRecordRows,
    recordTotal: results.previewRecordTotal,
  })

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!visibleFilters.includes(activeFilter)) setActiveFilter('all')
  }, [activeFilter, visibleFilters])

  const moveActiveFilter = (direction: -1 | 1) => {
    setActiveFilter((current) => {
      const currentIndex = Math.max(0, visibleFilters.indexOf(current))
      const nextIndex = Math.max(0, Math.min(currentIndex + direction, visibleFilters.length - 1))
      return visibleFilters[nextIndex] ?? 'all'
    })
  }

  const toggleSectionRows = (
    section: Section,
    action: ToggleAction,
    options?: { focusToggleAfterCollapse?: boolean }
  ) => {
    if (isMetadataKind(section.kind)) {
      if (action === 'more') results.revealMore(section.kind)
      else results.seeLess(section.kind)
      return
    }
    const willExpand = !navigation.expanded.has(section.key)
    navigation.toggleExpanded(section.key, options)
    if (section.kind === 'records' && isPreview && action === 'toggle' && willExpand) {
      results.loadRecordsAhead(section.rows.length + RECORD_PAGE_SIZE)
    }
  }

  const closeMatchPanel = () => {
    matchPanel.setOpen(false)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      if (matchPanel.open) closeMatchPanel()
      else onClose()
      return
    }
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault()
      moveActiveFilter(e.key === 'ArrowRight' ? 1 : -1)
      return
    }
    if (navigation.activeItemCount === 0) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      navigation.moveActiveIndex(e.key === 'ArrowDown' ? 1 : -1)
      return
    }
    if (e.key !== 'Enter') return
    e.preventDefault()
    if (tabSection) {
      const row = getTabRow(tabSection, activeIndex)
      if (row) onSelect(row.selection)
      return
    }
    const item = navigation.items[activeIndex]
    if (item?.type === 'section') navigation.toggleSectionCollapsed(item.sectionKey)
    if (item?.type === 'row') onSelect(item.row.selection)
    if (item?.type === 'toggle') {
      const section = sections.find((candidate) => candidate.key === item.sectionKey)
      if (section) {
        toggleSectionRows(section, item.action, {
          focusToggleAfterCollapse: navigation.expanded.has(item.sectionKey),
        })
      }
    }
  }

  const hasSections = sections.length > 0
  const showValuesPreviewTip = isPreview && activeFilter === 'values'
  const showsOnlyValuesTip = showValuesPreviewTip && !hasSections

  return (
    <PopupLayer layer={layers.dialogMenu}>
      <div
        className="fixed inset-0 z-[var(--layer-dialog)]"
        onPointerDown={() => {
          if (matchPanel.open) matchPanel.setOpen(false)
          else onClose()
        }}
      >
        <SearchPopoverFrame
          role="dialog"
          aria-label="Global search"
          style={{
            position: 'fixed',
            left: anchor.left,
            top: anchor.top,
            width: anchor.width,
            height: `min(720px, calc(100vh - ${anchor.top + 24}px))`,
          }}
          onPointerDown={(e) => {
            if (matchPanel.open && !matchPanel.isOwnTarget(e.target as Node)) {
              matchPanel.setOpen(false)
            }
            e.stopPropagation()
          }}
          onKeyDown={handleKeyDown}
        >
          <GlobalSearchInputBar
            height={anchor.height}
            inputRef={inputRef}
            value={input}
            onChange={setInput}
            onClear={() => {
              clear()
              navigation.setActiveIndex(0)
              requestAnimationFrame(() => inputRef.current?.focus())
            }}
            matchButtonRef={matchPanel.buttonRef}
            matchPanelOpen={matchPanel.open}
            onToggleMatchPanel={() => matchPanel.setOpen((open) => !open)}
          />

          <SearchPopoverFilters
            ariaLabel="Global search result type"
            activeKey={activeFilter}
            items={visibleFilters.map((key) => ({
              key,
              label: FILTER_LABELS[key],
              count: key === 'all' ? undefined : results.counts[key],
              countSuffix: isPreview && key === 'values' ? '+' : undefined,
            }))}
            onSelect={setActiveFilter}
          />

          <div
            ref={resultsRef}
            className={clsx(
              'min-h-0 flex-1 px-3 pb-3 [overflow-anchor:none]',
              showsOnlyValuesTip
                ? 'overflow-hidden'
                : 'popover-selector-scrollbar scroll-pt-3 overflow-y-auto'
            )}
            onScroll={(event) => navigation.setScrollTop(event.currentTarget.scrollTop)}
          >
            {!showsOnlyValuesTip && (
              <div
                className="surface-default sticky top-0 z-40 -mx-3 h-3 shrink-0"
                aria-hidden="true"
              />
            )}
            <GlobalSearchStatus
              query={query}
              isPreview={isPreview}
              isLoading={isLoading}
              hasSections={hasSections}
              showValuesPreviewTip={showValuesPreviewTip}
            />
            {sections.map((section, sectionIndex) => (
              <GlobalSearchSection
                key={section.key}
                section={section}
                sectionIndex={sectionIndex}
                isAllTab={isAllTab}
                isCollapsed={isAllTab && navigation.collapsed.has(section.key)}
                isExpanded={navigation.expanded.has(section.key)}
                rendered={navigation.getRenderedRows(section, (index) => getTabRow(section, index))}
                activeIndex={activeIndex}
                indexOf={navigation.indexOf}
                onActivate={navigation.setActiveIndex}
                onSelect={onSelect}
                onToggleCollapsed={navigation.toggleSectionCollapsed}
                onToggleRows={toggleSectionRows}
              />
            ))}
          </div>

          <GlobalSearchFooter />
        </SearchPopoverFrame>

        {matchPanel.open && (
          <SearchMatchOptionsPanel
            ref={matchPanel.panelRef}
            mode={matchMode}
            matchTypeOpen={matchPanel.matchTypeOpen}
            onModeChange={(partial) => setMatchMode((current) => ({ ...current, ...partial }))}
            onMatchTypeOpenChange={matchPanel.setMatchTypeOpen}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault()
                closeMatchPanel()
              }
            }}
            className="z-[var(--layer-dialog-menu)]"
            style={getMatchPanelStyle(anchor, matchPanel.buttonRef.current)}
          />
        )}
      </div>
    </PopupLayer>
  )
}
